import { MealTypes } from "../../../../server/constants";
import { getSchoolGrades, groupBy } from "../../../../server/util";



export const LabelOptions = {
    schoolName: "School",
    title: "Student Name",
    schoolStudentId: "Student Id",
    grade: "Grade",
    teacherName: "Teacher Name",
    selectedDate: "Meal Date",
    items: "Menu Items",
    allergies: "Allergies"
}

/**
 * Physical die-cut geometry of each supported label stock, in inches.
 * dx/dy are the pitch (label edge to the same edge of the next label), not the gap.
 * Every one of these sheets is centred on the page, so the grid origin is derived
 * from the page size rather than hard-coded - that also keeps A4 usable.
 */
export const LabelSheetSpecs = {
    // Avery 22806 / 22816 / 22853 / 94107 - 2" x 2", 12 per sheet
    1: { labelW: 2, labelH: 2, cols: 3, rows: 4, dx: 2.625, dy: 2.6, padX: 0.1, padY: 0.09 },
    // Avery 5160 / 5960 / 8160 / 94200 - 1" x 2-5/8", 30 per sheet
    2: { labelW: 2.625, labelH: 1, cols: 3, rows: 10, dx: 2.78125, dy: 1, padX: 0.1, padY: 0.04 },
    // Avery 6871 / 94227 - 1-1/4" x 2-3/8", 18 per sheet
    3: { labelW: 2.375, labelH: 1.25, cols: 3, rows: 6, dx: 2.69, dy: 1.5, padX: 0.09, padY: 0.06 }
}

// labelSize arrives from a <select> and from the API, so it can be a string.
const getSheetSpec = (labelSize) => LabelSheetSpecs[Number(labelSize)] || LabelSheetSpecs[1];

const toInches = (value, fallback) => {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? fallback : parsed;
}

const getPageInches = (pageSize) => ({
    w: toInches((pageSize || {}).w, 8.5),
    h: toInches((pageSize || {}).h, 11)
});

const inch = (value) => `${Math.round(value * 10000) / 10000}in`;

/**
 * Stylesheet for the print frame. `@page { margin: 0 }` is what stops the browser
 * from adding its own page margin on top of the Avery origin and pushing every
 * label off its die-cut.
 */
export const preparePrintLabelStyles = ({ pageSize }) => {
    const page = getPageInches(pageSize);
    return `<style type="text/css">
        @page { size: ${inch(page.w)} ${inch(page.h)}; margin: 0; }
        html, body { margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .mm-sheet { position: relative; box-sizing: border-box; width: ${inch(page.w)}; height: ${inch(page.h)}; overflow: hidden; page-break-after: always; break-after: page; }
        .mm-sheet:last-of-type { page-break-after: auto; break-after: auto; }
        .mm-label { position: absolute; box-sizing: border-box; overflow: hidden; page-break-inside: avoid; break-inside: avoid; font-family: Arial, Helvetica, sans-serif; }
        .mm-label ul { list-style: disc outside; margin: 2px 0 0 0; padding: 0 0 0 0.13in; }
        .mm-label li { word-wrap: break-word; }
    </style>`;
}

const buildLabelObjects = (args) => {
    const { grade, _gradeValueSortList, data, onlyExtraItems, showEntree, showAlaCarte, labelByItem } = args;
    const _grade = grade || [];
    const _data = (data || []).filter((itm) => {
        if (_grade.length === 0) {
            return true;
        } else if (_grade.includes(itm.gradeName)) {
            return true;
        } else {
            return false;
        }
    });
    const labels = [];
    _gradeValueSortList.map(grd => {
        const _gradeGroup = groupBy(_data, 'gradeName');
        if ((_gradeGroup[grd] || []).length > 0) {
            const selectedGroupData = (_gradeGroup[grd] || [])
            const teacherGroup = groupBy(selectedGroupData, 'teacherName');
            const sortedTeachers = Object.keys(teacherGroup || {}).sort((a, b) => {
                if (a < b) return -1;
                if (a > b) return 1;
                return 0;
            });
            sortedTeachers.map(teacher => {
                const tdata = (teacherGroup[teacher] || []).sort((a, b) => {
                    const aName = `${a.lastName} ${a.firstName}`;
                    const bName = `${b.lastName} ${b.firstName}`;
                    if (aName < bName) return -1;
                    if (aName > bName) return 1;
                    return 0;
                });
                tdata.map((item) => {
                    let menuObjItems = [];
                    if (onlyExtraItems) {
                        ((item.menuByType || {})['EXTRA'] || []).map(meal => {
                            menuObjItems.push({ item: `${meal.mealName} -${meal.qty}`, date: meal.mealDate });
                        });
                    } else {
                        if (showEntree !== false) {
                            [...((item.menuByType || {})['ENTREE'] || []), ...((item.menuByType || {})['MEAL'] || [])].map(meal => {
                                menuObjItems.push({ item: `${meal.mealName} -${meal.qty}`, date: meal.mealDate });
                            });
                        }
                        ((item.menuByType || {})[MealTypes.SNACK.toUpperCase()] || []).map(meal => {
                            menuObjItems.push({ item: `${meal.mealName} -${meal.qty}`, date: meal.mealDate });
                        });
                        ((item.menuByType || {})['SIDE'] || []).map(side => {
                            menuObjItems.push({ item: `${side.mealName} -${side.qty}`, date: side.mealDate });
                        });
                        if (showAlaCarte !== false) {
                            ((item.menuByType || {})['EXTRA'] || []).map(meal => {
                                menuObjItems.push({ item: `${meal.mealName} -${meal.qty}`, date: meal.mealDate });
                            });
                        }
                        ((item.menuByType || {})[MealTypes.BREAKFAST.toUpperCase()] || []).map(side => {
                            menuObjItems.push({ item: `${side.mealName} -${side.qty}`, date: side.mealDate });
                        });
                    }

                    const dateGroup = groupBy(menuObjItems, 'date');
                    Object.keys(dateGroup).map((dt) => {
                        const baseObj = {
                            title: `${item.lastName}, ${item.firstName}`,
                            sid: item.schoolStudentId,
                            schoolName: item.schoolName,
                            grade: item.gradeName,
                            teacherName: item.teacherName,
                            selectedDate: dt,
                            allergies: item.allergies
                        };
                        const labelObjs = (onlyExtraItems && labelByItem)
                            ? (dateGroup[dt] || []).map(itm => ({ ...baseObj, items: [itm.item] }))
                            : [{ ...baseObj, items: (dateGroup[dt] || []).map(itm => itm.item) }];
                        labelObjs.map(lblObj => {
                            if (lblObj.items.length > 0) {
                                labels.push(lblObj);
                            }
                        });
                    })
                });
            });
        }
    });
    return labels;
}

export const preparePrintLabelPageContent = (args) => {
    const spec = getSheetSpec(args.labelSize);
    const page = getPageInches(args.pageSize);
    const fontSize = args.fontSize || 10;
    const labels = buildLabelObjects(args);

    const perSheet = spec.cols * spec.rows;
    const gridW = (spec.dx * (spec.cols - 1)) + spec.labelW;
    const gridH = (spec.dy * (spec.rows - 1)) + spec.labelH;
    // The Avery origin for all three stocks is exactly the centred grid origin.
    const x0 = Math.max(0, (page.w - gridW) / 2);
    const y0 = Math.max(0, (page.h - gridH) / 2);

    let content = '';
    for (let start = 0; start < labels.length; start += perSheet) {
        content = content + '<div class="mm-sheet">';
        labels.slice(start, start + perSheet).forEach((lblObj, idx) => {
            const col = idx % spec.cols;
            const row = Math.floor(idx / spec.cols);
            const position = `left:${inch(x0 + (col * spec.dx))};top:${inch(y0 + (row * spec.dy))};` +
                `width:${inch(spec.labelW)};height:${inch(spec.labelH)};padding:${inch(spec.padY)} ${inch(spec.padX)};`;
            content = content + `<div class="mm-label" style="${position}">${createLabel({ ...args, lblObj, fontSize })}</div>`;
        });
        content = content + '</div>';
    }
    return content;
}

export const createLabel = ({ lblObj, selectedOptions, isCaterer, schoolId, fontSize = 10 }) => {
    const { title, sid, grade, items, teacherName, schoolName, selectedDate, allergies } = lblObj;
    const gradeGroup = groupBy(getSchoolGrades((isCaterer ? schoolId : 0), false), 'value');
    const gradeText = ((gradeGroup[grade] || [])[0] || {}).label || ''
    // Line height has to track the chosen font size, otherwise shrinking the font
    // frees no vertical room on the short 1" and 1-1/4" stocks.
    const lineHeight = Math.round(fontSize * 1.35);
    const shows = (key) => (selectedOptions || []).includes(key);

    let itemListElements = '';
    items.map(itm => {
        itemListElements = itemListElements + `<li style="line-height: ${lineHeight}px;">${itm}</li>`
    })

    const headingStyle = `font-size:${fontSize + 2}px; font-weight:bold; line-height: ${lineHeight}px; text-transform: uppercase; word-wrap: break-word`;
    const detailStyle = `font-size:${fontSize}px; font-weight:bold; line-height: ${lineHeight}px;`;

    return `${shows('schoolName') ? `<span style="color:#f47e35; font-size:${fontSize - 1}px; font-weight:bold; line-height: ${lineHeight}px; text-transform: uppercase; word-wrap: break-word">${schoolName || ''}</span><br/>` : ''}${shows('title') ? `<span style="${headingStyle}">${title}</span>` : ''}${shows('schoolStudentId') ? shows('title') ? `<span style="${headingStyle}"> -[${sid}]</span>` : `<span style="${headingStyle}"> S.Id:${sid}</span>` : ''}${shows('title') || shows('schoolStudentId') ? `<br/>` : ''}${shows('grade') ? `<span style="${detailStyle}">Grade: ${gradeText}</span>${shows('selectedDate') ? `&nbsp;&nbsp;&nbsp;&nbsp;` : ''}` : ''}${shows('selectedDate') ? `<span style="${detailStyle}">Dt: ${selectedDate}</span>` : ''}${shows('grade') || shows('selectedDate') ? `<br/>` : ''}${shows('teacherName') ? `<span style="${detailStyle}">Teacher: ${teacherName}</span><br/>` : ''}${shows('allergies') && allergies && !allergies.includes(',None') ? `<span style="${detailStyle}">Allergies: ${allergies}</span><br/>` : ''}${shows('items') ? `<ul style="font-size:${fontSize + 1}px;">${itemListElements}</ul>` : ''}`;
}



// WEBPACK FOOTER //
// ./src/components/SchoolAdmin/Reports/printLabelsTemplates/printLabelTemplate.js